import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Camera, Save, ArrowLeft, Plus, Trash2, 
  IndianRupee, Briefcase, GraduationCap, Languages, 
  MapPin, Phone, Globe, FileText, X, Upload, Image
} from 'lucide-react';
import { toast } from 'sonner';
import { api, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

const SPECIALIZATIONS = [
  'Criminal',
  'Civil',
  'Corporate',
  'Family',
  'Intellectual Property',
  'Cyber Law',
  'Property',
  'Tax',
  'Immigration'
];

const LANGUAGES = ['English', 'Hindi', 'Marathi', 'Gujarati', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Bengali', 'Punjabi'];

const LawyerProfileSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    consultation_fee: '',
    years_of_experience: '',
    qualification: '',
    bio: '',
    specializations: [],
    languages: [],
    office_address: '',
    office_phone: '',
    website: '',
    bar_council_number: ''
  });
  
  // User contact state
  const [contactData, setContactData] = useState({
    mobile: '',
    location: ''
  });
  
  // Gallery modal
  const [galleryModal, setGalleryModal] = useState(false);
  const [galleryCaption, setGalleryCaption] = useState('');
  const [selectedGalleryFile, setSelectedGalleryFile] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await api.get('/lawyers/profile/me');
      setProfile(res.data);
      
      // Set form data from profile
      setFormData({
        consultation_fee: res.data.consultation_fee || '',
        years_of_experience: res.data.years_of_experience || '',
        qualification: res.data.qualification || '',
        bio: res.data.bio || '',
        specializations: res.data.specializations || [],
        languages: res.data.languages || [],
        office_address: res.data.office_address || '',
        office_phone: res.data.office_phone || '',
        website: res.data.website || '',
        bar_council_number: res.data.bar_council_number || ''
      });
      
      // Set contact data from user
      setContactData({
        mobile: res.data.user?.mobile || '',
        location: res.data.user?.location || ''
      });
    } catch (err) {
      console.error('Error fetching profile:', err);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      // Update lawyer profile
      await api.patch('/lawyers/profile', {
        consultation_fee: parseFloat(formData.consultation_fee) || 0,
        years_of_experience: parseInt(formData.years_of_experience) || 0,
        qualification: formData.qualification,
        bio: formData.bio,
        specializations: formData.specializations,
        languages: formData.languages,
        office_address: formData.office_address,
        office_phone: formData.office_phone,
        website: formData.website,
        bar_council_number: formData.bar_council_number
      });
      
      // Update contact details
      await api.patch('/lawyers/user/contact', {
        mobile: contactData.mobile,
        location: contactData.location
      });
      
      toast.success('Profile updated successfully!');
      fetchProfile();
    } catch (err) {
      console.error('Error saving profile:', err);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, GIF, or WebP');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum 5MB allowed');
      return;
    }
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      await api.post('/lawyers/profile/photo', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Profile photo uploaded!');
      fetchProfile();
    } catch (err) {
      console.error('Error uploading photo:', err);
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async () => {
    if (!selectedGalleryFile) {
      toast.error('Please select a file');
      return;
    }
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', selectedGalleryFile);
      formDataUpload.append('caption', galleryCaption);
      
      await api.post('/lawyers/profile/gallery', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Gallery photo uploaded!');
      setGalleryModal(false);
      setSelectedGalleryFile(null);
      setGalleryCaption('');
      fetchProfile();
    } catch (err) {
      console.error('Error uploading gallery photo:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteGalleryPhoto = async (photoId) => {
    if (!window.confirm('Remove this photo from your gallery?')) return;
    
    try {
      await api.delete(`/lawyers/profile/gallery/${photoId}`);
      toast.success('Photo removed');
      fetchProfile();
    } catch (err) {
      toast.error('Failed to remove photo');
    }
  };

  const toggleSpecialization = (spec) => {
    setFormData(prev => ({
      ...prev,
      specializations: prev.specializations.includes(spec)
        ? prev.specializations.filter(s => s !== spec)
        : [...prev.specializations, spec]
    }));
  };

  const toggleLanguage = (lang) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter(l => l !== lang)
        : [...prev.languages, lang]
    }));
  };

  const getPhotoUrl = (path) => {
    if (!path) return null;
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${baseUrl}/api/lawyers/photos/${path}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="container-app py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-40 bg-slate-200 rounded-2xl" />
            <div className="h-60 bg-slate-200 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="container-app py-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/lawyer/dashboard')}
            className="p-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Profile Settings</h1>
            <p className="text-slate-500">Manage your professional profile</p>
          </div>
        </div>

        {/* Profile Photo Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-slate-200 p-6 mb-6"
        >
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Profile Photo</h2>
          <div className="flex items-center gap-6">
            <div className="relative">
              <div className="w-32 h-32 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden border-4 border-white shadow-lg">
                {profile?.profile_photo ? (
                  <img
                    src={getPhotoUrl(profile.profile_photo)}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Camera className="w-12 h-12 text-slate-400" />
                )}
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="absolute bottom-0 right-0 w-10 h-10 bg-amber-600 rounded-full flex items-center justify-center text-white hover:bg-amber-700 transition-colors shadow-lg"
                data-testid="upload-photo-btn"
              >
                {uploading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Camera className="w-5 h-5" />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfilePhotoUpload}
                className="hidden"
              />
            </div>
            <div>
              <p className="text-sm text-slate-600">Upload a professional headshot</p>
              <p className="text-xs text-slate-400 mt-1">JPG, PNG, GIF or WebP. Max 5MB</p>
            </div>
          </div>
        </motion.div>

        {/* Gallery Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl border border-slate-200 p-6 mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Photo Gallery</h2>
              <p className="text-sm text-slate-500">Add photos of your office, certificates, etc.</p>
            </div>
            <Button
              onClick={() => setGalleryModal(true)}
              disabled={(profile?.gallery?.length || 0) >= 10}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="add-gallery-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Photo
            </Button>
          </div>
          
          {profile?.gallery?.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {profile.gallery.map((photo, idx) => (
                <div
                  key={photo.id}
                  className="relative group aspect-square rounded-xl overflow-hidden bg-slate-100"
                  data-testid={`gallery-photo-${idx}`}
                >
                  <img
                    src={getPhotoUrl(photo.path)}
                    alt={photo.caption || 'Gallery photo'}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button
                      onClick={() => handleDeleteGalleryPhoto(photo.id)}
                      className="p-2 bg-red-600 rounded-full text-white hover:bg-red-700"
                      data-testid={`delete-gallery-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {photo.caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <p className="text-xs text-white truncate">{photo.caption}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-slate-50 rounded-xl">
              <Image className="w-12 h-12 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500">No gallery photos yet</p>
            </div>
          )}
          <p className="text-xs text-slate-400 mt-3">{profile?.gallery?.length || 0}/10 photos</p>
        </motion.div>

        {/* Consultation Fee */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl border border-slate-200 p-6 mb-6"
        >
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-amber-600" />
            Consultation Fee
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-slate-500">₹</span>
            <Input
              type="number"
              value={formData.consultation_fee}
              onChange={(e) => setFormData(prev => ({ ...prev, consultation_fee: e.target.value }))}
              placeholder="Enter your consultation fee"
              className="max-w-xs"
              data-testid="fee-input"
            />
            <span className="text-slate-500">per session</span>
          </div>
        </motion.div>

        {/* Professional Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl border border-slate-200 p-6 mb-6"
        >
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-amber-600" />
            Professional Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Years of Experience</label>
              <Input
                type="number"
                value={formData.years_of_experience}
                onChange={(e) => setFormData(prev => ({ ...prev, years_of_experience: e.target.value }))}
                placeholder="e.g., 10"
                data-testid="experience-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Bar Council Number</label>
              <Input
                value={formData.bar_council_number}
                onChange={(e) => setFormData(prev => ({ ...prev, bar_council_number: e.target.value }))}
                placeholder="e.g., MH/12345/2015"
                data-testid="bar-council-input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Qualification</label>
              <Input
                value={formData.qualification}
                onChange={(e) => setFormData(prev => ({ ...prev, qualification: e.target.value }))}
                placeholder="e.g., LLB, LLM (Criminal Law)"
                data-testid="qualification-input"
              />
            </div>
          </div>
        </motion.div>

        {/* Specializations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl border border-slate-200 p-6 mb-6"
        >
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-amber-600" />
            Specializations
          </h2>
          <div className="flex flex-wrap gap-2">
            {SPECIALIZATIONS.map((spec) => (
              <button
                key={spec}
                type="button"
                onClick={() => toggleSpecialization(spec)}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${
                  formData.specializations.includes(spec)
                    ? 'bg-amber-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`spec-${spec.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {spec}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Languages */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl border border-slate-200 p-6 mb-6"
        >
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Languages className="w-5 h-5 text-amber-600" />
            Languages
          </h2>
          <div className="flex flex-wrap gap-2">
            {LANGUAGES.map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => toggleLanguage(lang)}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${
                  formData.languages.includes(lang)
                    ? 'bg-amber-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`lang-${lang.toLowerCase()}`}
              >
                {lang}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Bio */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-2xl border border-slate-200 p-6 mb-6"
        >
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-amber-600" />
            Professional Bio
          </h2>
          <Textarea
            value={formData.bio}
            onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
            placeholder="Write about your experience, achievements, and what makes you stand out..."
            rows={5}
            data-testid="bio-input"
          />
          <p className="text-xs text-slate-400 mt-2">{formData.bio?.length || 0}/1000 characters</p>
        </motion.div>

        {/* Contact Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-2xl border border-slate-200 p-6 mb-6"
        >
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Phone className="w-5 h-5 text-amber-600" />
            Contact Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mobile Number</label>
              <Input
                type="tel"
                value={contactData.mobile}
                onChange={(e) => setContactData(prev => ({ ...prev, mobile: e.target.value }))}
                placeholder="+91 9876543210"
                data-testid="mobile-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Office Phone</label>
              <Input
                value={formData.office_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, office_phone: e.target.value }))}
                placeholder="022-12345678"
                data-testid="office-phone-input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                <MapPin className="w-4 h-4 inline mr-1" />
                Location / City
              </label>
              <Input
                value={contactData.location}
                onChange={(e) => setContactData(prev => ({ ...prev, location: e.target.value }))}
                placeholder="Mumbai, Maharashtra"
                data-testid="location-input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Office Address</label>
              <Textarea
                value={formData.office_address}
                onChange={(e) => setFormData(prev => ({ ...prev, office_address: e.target.value }))}
                placeholder="Full office address..."
                rows={2}
                data-testid="address-input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                <Globe className="w-4 h-4 inline mr-1" />
                Website
              </label>
              <Input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://yourwebsite.com"
                data-testid="website-input"
              />
            </div>
          </div>
        </motion.div>

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="flex justify-end gap-4"
        >
          <Button
            variant="outline"
            onClick={() => navigate('/lawyer/dashboard')}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveProfile}
            disabled={saving}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="save-profile-btn"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </motion.div>
      </div>

      {/* Gallery Upload Modal */}
      <Dialog open={galleryModal} onOpenChange={setGalleryModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Gallery Photo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div
              className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-amber-500 transition-colors"
              onClick={() => galleryInputRef.current?.click()}
            >
              {selectedGalleryFile ? (
                <div className="flex items-center justify-center gap-3">
                  <Image className="w-8 h-8 text-amber-600" />
                  <span className="text-slate-700">{selectedGalleryFile.name}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedGalleryFile(null);
                    }}
                    className="p-1 hover:bg-slate-100 rounded"
                  >
                    <X className="w-4 h-4 text-slate-500" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                  <p className="text-slate-600">Click to select a photo</p>
                  <p className="text-xs text-slate-400 mt-1">JPG, PNG, GIF or WebP. Max 5MB</p>
                </>
              )}
            </div>
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => setSelectedGalleryFile(e.target.files?.[0] || null)}
              className="hidden"
            />
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Caption (optional)</label>
              <Input
                value={galleryCaption}
                onChange={(e) => setGalleryCaption(e.target.value)}
                placeholder="e.g., My office, Certificate of Excellence..."
                data-testid="gallery-caption-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGalleryModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleGalleryUpload}
              disabled={!selectedGalleryFile || uploading}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="upload-gallery-btn"
            >
              {uploading ? 'Uploading...' : 'Upload Photo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LawyerProfileSettings;
